<template>
  <div>
    <!-- Header -->
    <div class="page-header">
      <div class="header-left">
        <button class="back-btn" @click="$router.push($url('/hr/exits'))">
          <font-awesome-icon icon="arrow-left" />
        </button>
        <div>
          <div class="case-title">
            <span class="case-no">{{ exitCase?.case_number }}</span>
            <span v-if="exitCase" class="status-badge" :class="'status--' + exitCase.status">{{ statusLabel(exitCase?.status) }}</span>
          </div>
          <p class="page-sub">{{ fullName(exitCase?.employee) }}</p>
        </div>
      </div>
      <div class="header-actions">
        <template v-if="exitCase && canTransition">
          <button v-if="exitCase.status === 'approved'" class="btn-action" @click="doTransition('move_to_clearance')">Start Clearance</button>
          <button v-if="exitCase.status === 'clearance'" class="btn-action" @click="doTransition('move_to_fnf')">Move to F&F</button>
          <button v-if="!['completed','cancelled','draft','pending_approval'].includes(exitCase.status)" class="btn-success" @click="openCompleteModal">Complete Exit</button>
          <button v-if="!['completed','cancelled'].includes(exitCase.status)" class="btn-danger-outline" @click="showCancelModal = true">Cancel Exit</button>
        </template>
      </div>
    </div>

    <div v-if="loading" class="sk-page">
      <div class="sk-header"></div>
      <div class="sk-body"></div>
    </div>

    <div v-else-if="exitCase">
      <!-- Employee info strip -->
      <div class="emp-strip">
        <div class="emp-avatar-lg">{{ initials(exitCase.employee) }}</div>
        <div class="emp-info">
          <div class="emp-name-lg">{{ fullName(exitCase.employee) }}</div>
          <div class="emp-meta">
            <span v-if="exitCase.employee?.employee_no">{{ exitCase.employee.employee_no }}</span>
            <span v-if="exitCase.employee?.department?.name" class="dot">·</span>
            <span v-if="exitCase.employee?.department?.name">{{ exitCase.employee.department.name }}</span>
          </div>
        </div>
        <div class="kpi-pills">
          <div class="kpi-pill">
            <div class="kpi-label">Type</div>
            <div class="kpi-value"><span class="type-badge" :class="'type--' + exitCase.type">{{ typeLabel(exitCase.type) }}</span></div>
          </div>
          <div class="kpi-pill">
            <div class="kpi-label">Exit Date</div>
            <div class="kpi-value">{{ fmtDate(exitCase.exit_date) }}</div>
          </div>
          <div class="kpi-pill">
            <div class="kpi-label">Last Working Day</div>
            <div class="kpi-value">{{ fmtDate(exitCase.last_working_day) }}</div>
          </div>
          <div class="kpi-pill">
            <div class="kpi-label">Notice Days</div>
            <div class="kpi-value">{{ exitCase.notice_period_days ?? '—' }}</div>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="tabs-row">
        <button v-for="tab in tabs" :key="tab.key" class="tab-btn" :class="{ 'tab-btn--active': activeTab === tab.key }" @click="switchTab(tab.key)">
          {{ tab.label }}
        </button>
      </div>

      <!-- Tab: Overview -->
      <div v-if="activeTab === 'overview'" class="tab-body">
        <div class="detail-grid">
          <div class="detail-block">
            <div class="block-label">Exit Date</div>
            <div class="block-val">{{ fmtDate(exitCase.exit_date) }}</div>
          </div>
          <div class="detail-block">
            <div class="block-label">Last Working Day</div>
            <div class="block-val">{{ fmtDate(exitCase.last_working_day) }}</div>
          </div>
          <div class="detail-block">
            <div class="block-label">Notice Period</div>
            <div class="block-val">{{ exitCase.notice_period_days != null ? exitCase.notice_period_days + ' days' : '—' }}</div>
          </div>
          <div class="detail-block">
            <div class="block-label">Reason</div>
            <div class="block-val">{{ exitCase.reason || '—' }}</div>
          </div>
          <div class="detail-block">
            <div class="block-label">Confidential Notes</div>
            <div class="block-val">{{ exitCase.confidential_notes || '—' }}</div>
          </div>
          <div class="detail-block">
            <div class="block-label">Initiated By</div>
            <div class="block-val">{{ fullName(exitCase.initiated_by) }} ({{ exitCase.initiated_source?.toUpperCase() }})</div>
          </div>
          <div class="detail-block">
            <div class="block-label">Access Revoked</div>
            <div class="block-val">{{ exitCase.access_revoked_at ? fmtDate(exitCase.access_revoked_at) : 'Not yet' }}</div>
          </div>
        </div>

        <!-- Edit basic details -->
        <div v-if="canEdit" class="edit-section">
          <div class="section-label">Edit Details</div>
          <div class="form-grid-2">
            <div class="form-row">
              <label class="form-label">Exit Date</label>
              <input v-model="editForm.exit_date" type="date" class="form-input" />
            </div>
            <div class="form-row">
              <label class="form-label">Last Working Day</label>
              <input v-model="editForm.last_working_day" type="date" class="form-input" />
            </div>
          </div>
          <div class="form-row" style="margin-top:14px;">
            <label class="form-label">Notice Period (days)</label>
            <input v-model.number="editForm.notice_period_days" type="number" min="0" max="365" class="form-input" style="max-width:160px;" />
          </div>
          <div class="form-row" style="margin-top:14px;">
            <label class="form-label">Reason</label>
            <textarea v-model="editForm.reason" class="form-input" rows="3" style="resize:vertical;"></textarea>
          </div>
          <div class="form-row" style="margin-top:14px;">
            <label class="form-label">Confidential Notes</label>
            <textarea v-model="editForm.confidential_notes" class="form-input" rows="3" style="resize:vertical;"></textarea>
          </div>
          <div style="display:flex;justify-content:flex-end;margin-top:14px;">
            <button class="btn-primary-action" @click="saveDetails" :disabled="savingDetails">
              <font-awesome-icon v-if="savingDetails" icon="spinner" spin />
              {{ savingDetails ? 'Saving…' : 'Save Changes' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Tab: Clearance -->
      <div v-if="activeTab === 'clearance'" class="tab-body">
        <div class="tab-top-bar">
          <div class="tab-title">Clearance Tasks</div>
          <button v-if="canEdit" class="btn-sm-primary" @click="showAddTask = true">+ Add Task</button>
        </div>
        <div v-if="loadingTasks" class="sk-list"><div v-for="n in 4" :key="n" class="sk-item"></div></div>
        <div v-else>
          <div v-if="!tasks.length" class="empty-state">No clearance tasks. Add tasks or advance to generate defaults.</div>
          <div v-for="task in tasks" :key="task.id" class="task-card">
            <div class="task-left">
              <span class="task-dept">{{ task.department }}</span>
              <span class="task-name">{{ task.task_name }}</span>
              <span v-if="task.assigned_to" class="task-assign">Assigned: {{ fullName(task.assigned_to) }}</span>
            </div>
            <div class="task-right">
              <span class="task-status" :class="'task-st--' + task.status">{{ task.status }}</span>
              <select v-if="canEdit" v-model="task.status" class="task-select" @change="updateTask(task)">
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="waived">Waived</option>
              </select>
            </div>
          </div>
          <div class="clearance-summary">
            <span class="cs-done">{{ doneCount }} done</span> / {{ tasks.length }} tasks
          </div>
        </div>
        <!-- Add task modal -->
        <div v-if="showAddTask" class="dlg-overlay" @click.self="showAddTask=false">
          <div class="dlg-box">
            <div class="dlg-title">Add Clearance Task</div>
            <div class="form-row" style="margin-bottom:12px;">
              <label class="form-label">Department</label>
              <input v-model="newTask.department" class="form-input" placeholder="e.g. IT, Finance" />
            </div>
            <div class="form-row" style="margin-bottom:12px;">
              <label class="form-label">Task</label>
              <input v-model="newTask.task_name" class="form-input" placeholder="Task description" />
            </div>
            <div class="form-row" style="margin-bottom:16px;">
              <label class="form-label">Assigned To</label>
              <search-select
                v-model="newTask.assigned_to_employee_id"
                url="payroll/employees/active"
                query-param="search"
                label-key="full_name"
                sublabel-key="employee_no"
                value-key="id"
                response-key="data"
                placeholder="— Select employee (optional) —"
                search-placeholder="Type name or number…"
                :min-chars="1"
              />
            </div>
            <div class="dlg-actions">
              <button class="btn-outline" @click="showAddTask=false">Cancel</button>
              <button class="btn-primary-action" @click="addTask" :disabled="savingTask">Add</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab: Knowledge Transfer -->
      <div v-if="activeTab === 'kt'" class="tab-body">
        <div class="tab-top-bar">
          <div class="tab-title">Knowledge Transfer</div>
          <button v-if="canEdit" class="btn-sm-primary" @click="showAddKt = true">+ Add Task</button>
        </div>
        <div v-if="loadingKt" class="sk-list"><div v-for="n in 3" :key="n" class="sk-item"></div></div>
        <div v-else>
          <div v-if="!ktLogs.length" class="empty-state">No KT tasks assigned yet.</div>
          <div v-for="log in ktLogs" :key="log.id" class="task-card">
            <div class="task-left">
              <span class="task-name">{{ log.task_description }}</span>
              <span class="task-assign">To: {{ fullName(log.to_employee) }}</span>
              <span v-if="log.due_date" class="task-assign">Due: {{ fmtDate(log.due_date) }}</span>
            </div>
            <div class="task-right">
              <select v-if="canEdit" v-model="log.status" class="task-select" @change="updateKt(log)">
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>
        </div>
        <!-- Add KT modal -->
        <div v-if="showAddKt" class="dlg-overlay" @click.self="showAddKt=false">
          <div class="dlg-box">
            <div class="dlg-title">Add KT Task</div>
            <div class="form-row" style="margin-bottom:12px;">
              <label class="form-label">Transfer To</label>
              <search-select
                v-model="newKt.to_employee_id"
                url="payroll/employees/active"
                query-param="search"
                label-key="full_name"
                sublabel-key="employee_no"
                value-key="id"
                response-key="data"
                placeholder="— Select employee —"
                search-placeholder="Type name or number…"
                :min-chars="1"
              />
            </div>
            <div class="form-row" style="margin-bottom:12px;">
              <label class="form-label">Task Description</label>
              <textarea v-model="newKt.task_description" class="form-input" rows="2"></textarea>
            </div>
            <div class="form-row" style="margin-bottom:16px;">
              <label class="form-label">Due Date</label>
              <input v-model="newKt.due_date" type="date" class="form-input" />
            </div>
            <div class="dlg-actions">
              <button class="btn-outline" @click="showAddKt=false">Cancel</button>
              <button class="btn-primary-action" @click="addKt" :disabled="savingKt">Add</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab: Exit Interview -->
      <div v-if="activeTab === 'interview'" class="tab-body">
        <div class="tab-title" style="margin-bottom:16px;">Exit Interview</div>
        <div v-if="loadingInterview" class="sk-list"><div class="sk-item"></div></div>
        <div v-else>
          <div class="form-grid-2">
            <div class="form-row">
              <label class="form-label">Scheduled At</label>
              <input v-model="interview.scheduled_at" type="datetime-local" class="form-input" :disabled="!canEdit" />
            </div>
            <div class="form-row">
              <label class="form-label">Conducted At</label>
              <input v-model="interview.conducted_at" type="datetime-local" class="form-input" :disabled="!canEdit" />
            </div>
          </div>
          <div class="form-row" style="margin-top:14px;">
            <label class="form-label">Overall Rating (1–5)</label>
            <div class="star-row">
              <button v-for="s in 5" :key="s" class="star-btn" :class="{ 'star--on': s <= (interview.overall_rating || 0) }" @click="canEdit && (interview.overall_rating = s)">★</button>
            </div>
          </div>
          <div class="form-row" style="margin-top:14px;">
            <label class="form-label">Notes</label>
            <textarea v-model="interview.notes" class="form-input" rows="4" :disabled="!canEdit" style="resize:vertical;"></textarea>
          </div>
          <div class="form-row" style="margin-top:14px;">
            <label class="form-label">Status</label>
            <select v-model="interview.status" class="form-input" :disabled="!canEdit" style="max-width:200px;">
              <option value="scheduled">Scheduled</option>
              <option value="completed">Completed</option>
              <option value="skipped">Skipped</option>
            </select>
          </div>
          <div v-if="canEdit" style="display:flex;justify-content:flex-end;margin-top:14px;">
            <button class="btn-primary-action" @click="saveInterview" :disabled="savingInterview">
              <font-awesome-icon v-if="savingInterview" icon="spinner" spin />
              {{ savingInterview ? 'Saving…' : 'Save Interview' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Tab: F&F Settlement -->
      <div v-if="activeTab === 'fnf'" class="tab-body">
        <div class="tab-top-bar">
          <div class="tab-title">Full & Final Settlement</div>
          <button v-if="canEdit" class="btn-sm-primary" @click="showAddFnf = true">+ Add Item</button>
        </div>
        <div v-if="loadingFnf" class="sk-list"><div v-for="n in 4" :key="n" class="sk-item"></div></div>
        <div v-else>
          <div class="fnf-table-wrap">
            <table class="fnf-table">
              <thead><tr><th>Description</th><th>Type</th><th>Amount</th><th v-if="canEdit"></th></tr></thead>
              <tbody>
                <tr v-if="!fnfItems.length"><td colspan="4" class="empty-row">No items yet.</td></tr>
                <tr v-for="item in fnfItems" :key="item.id">
                  <td>{{ item.description }}</td>
                  <td><span class="fnf-type" :class="'fnf--' + item.type">{{ item.type }}</span></td>
                  <td class="fnf-amt" :class="item.type === 'deduction' ? 'fnf-red' : 'fnf-green'">{{ fmtAmt(item.amount) }}</td>
                  <td v-if="canEdit"><button class="btn-del" @click="deleteFnf(item.id)">✕</button></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="fnf-summary">
            <div class="fnf-row"><span>Total Earnings</span><span class="fnf-green">{{ fmtAmt(fnfSummary.earnings) }}</span></div>
            <div class="fnf-row"><span>Total Deductions</span><span class="fnf-red">{{ fmtAmt(fnfSummary.deductions) }}</span></div>
            <div class="fnf-row fnf-net"><span>Net Payable</span><span>{{ fmtAmt(fnfSummary.net) }}</span></div>
          </div>
          <div v-if="canEdit && exitCase.status === 'fnf_pending'" style="display:flex;justify-content:flex-end;margin-top:16px;">
            <button class="btn-success" @click="processFnf" :disabled="processingFnf">
              <font-awesome-icon v-if="processingFnf" icon="spinner" spin />
              {{ processingFnf ? 'Processing…' : 'Process F&F Payment' }}
            </button>
          </div>
        </div>
        <!-- Add FnF modal -->
        <div v-if="showAddFnf" class="dlg-overlay" @click.self="showAddFnf=false">
          <div class="dlg-box">
            <div class="dlg-title">Add F&F Item</div>
            <div class="form-row" style="margin-bottom:12px;">
              <label class="form-label">Description</label>
              <input v-model="newFnf.description" class="form-input" />
            </div>
            <div class="form-grid-2" style="margin-bottom:12px;">
              <div class="form-row">
                <label class="form-label">Type</label>
                <select v-model="newFnf.type" class="form-input">
                  <option value="earning">Earning</option>
                  <option value="deduction">Deduction</option>
                </select>
              </div>
              <div class="form-row">
                <label class="form-label">Amount</label>
                <input v-model.number="newFnf.amount" type="number" min="0" step="0.01" class="form-input" />
              </div>
            </div>
            <div class="dlg-actions">
              <button class="btn-outline" @click="showAddFnf=false">Cancel</button>
              <button class="btn-primary-action" @click="addFnf" :disabled="savingFnf">Add</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab: Documents -->
      <div v-if="activeTab === 'documents'" class="tab-body">
        <div class="empty-state" style="padding:40px 0;">
          <font-awesome-icon icon="file-alt" style="font-size:32px;color:#dde5f0;margin-bottom:12px;" />
          <p>Document generation (offer letters, acceptance letters, service certificates) — coming in a future update.</p>
        </div>
      </div>

      <!-- Tab: Timeline -->
      <div v-if="activeTab === 'timeline'" class="tab-body">
        <div class="timeline">
          <div v-for="evt in timeline" :key="evt.label" class="tl-item">
            <div class="tl-dot" :class="evt.done ? 'tl-dot--done' : ''"></div>
            <div class="tl-content">
              <div class="tl-label">{{ evt.label }}</div>
              <div class="tl-date">{{ evt.date || '—' }}</div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Cancel modal -->
    <!-- Complete Exit modal -->
    <div v-if="showCompleteModal" class="dlg-overlay" @click.self="showCompleteModal=false">
      <div class="dlg-box">
        <div class="dlg-title">Complete Exit Case</div>
        <p style="font-size:13px;color:#4a5e78;margin-bottom:20px;">This will mark the exit as fully completed. Choose when to deactivate the employee's account.</p>
        <div class="radio-group">
          <label class="radio-option">
            <input type="radio" v-model="deactivateOption" value="now" />
            <span>Deactivate immediately</span>
          </label>
          <label class="radio-option" :class="{ 'radio-option--disabled': !exitCase.last_working_day }">
            <input type="radio" v-model="deactivateOption" value="scheduled" :disabled="!exitCase.last_working_day" />
            <span>Deactivate on last working day<span v-if="exitCase.last_working_day" class="opt-date"> ({{ fmtDate(exitCase.last_working_day) }})</span></span>
          </label>
          <label class="radio-option">
            <input type="radio" v-model="deactivateOption" value="skip" />
            <span>Skip — handle manually</span>
          </label>
        </div>
        <div class="dlg-actions" style="margin-top:20px;">
          <button class="btn-outline" @click="showCompleteModal=false">Cancel</button>
          <button class="btn-success" @click="doComplete" :disabled="completing">
            <font-awesome-icon v-if="completing" icon="spinner" spin />
            {{ completing ? 'Completing…' : 'Complete Exit' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showCancelModal" class="dlg-overlay" @click.self="showCancelModal=false">
      <div class="dlg-box">
        <div class="dlg-title">Cancel Exit Case</div>
        <div class="form-row" style="margin-bottom:16px;">
          <label class="form-label">Reason for cancellation</label>
          <textarea v-model="cancelReason" class="form-input" rows="3"></textarea>
        </div>
        <div class="dlg-actions">
          <button class="btn-outline" @click="showCancelModal=false">Back</button>
          <button class="btn-danger" @click="doTransition('cancel')">Confirm Cancel</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'
import SearchSelect from '../../../../components/SearchSelect.vue'

export default {
  name: 'ExitShow',
  components: { SearchSelect },
  setup() { return { toast: useToast() } },
  data() {
    return {
      loading: true,
      exitCase: null,
      activeTab: 'overview',
      tabs: [
        { key: 'overview',   label: 'Overview' },
        { key: 'clearance',  label: 'Clearance' },
        { key: 'kt',         label: 'Knowledge Transfer' },
        { key: 'interview',  label: 'Exit Interview' },
        { key: 'fnf',        label: 'F&F Settlement' },
        { key: 'documents',  label: 'Documents' },
        { key: 'timeline',   label: 'Timeline' },
      ],
      // edit
      editForm: {}, savingDetails: false,
      // clearance
      tasks: [], loadingTasks: false, showAddTask: false, savingTask: false,
      newTask: { department: '', task_name: '', assigned_to_employee_id: '' },
      // kt
      ktLogs: [], loadingKt: false, showAddKt: false, savingKt: false,
      newKt: { to_employee_id: '', task_description: '', due_date: '' },
      employees: [],
      // interview
      interview: {}, loadingInterview: false, savingInterview: false,
      // fnf
      fnfItems: [], fnfSummary: { earnings: 0, deductions: 0, net: 0 },
      loadingFnf: false, showAddFnf: false, savingFnf: false, processingFnf: false,
      newFnf: { description: '', type: 'earning', amount: '' },
      // cancel
      showCancelModal: false, cancelReason: '',
      showCompleteModal: false, deactivateOption: 'scheduled', completing: false,
    }
  },
  computed: {
    canEdit() {
      return this.exitCase && !['completed', 'cancelled'].includes(this.exitCase.status)
    },
    canTransition() {
      return !!this.exitCase
    },
    doneCount() {
      return this.tasks.filter(t => ['completed', 'waived'].includes(t.status)).length
    },
    timeline() {
      if (!this.exitCase) return []
      const c = this.exitCase
      return [
        { label: 'Case Created',        date: this.fmtDate(c.created_at),         done: true },
        { label: 'Approved',            date: c.status !== 'pending_approval' ? this.fmtDate(c.updated_at) : null, done: c.status !== 'pending_approval' && c.status !== 'draft' },
        { label: 'Clearance Started',   date: ['clearance','fnf_pending','completed'].includes(c.status) ? this.fmtDate(c.updated_at) : null, done: ['clearance','fnf_pending','completed'].includes(c.status) },
        { label: 'F&F Processing',      date: c.status === 'fnf_pending' || c.status === 'completed' ? this.fmtDate(c.updated_at) : null, done: c.status === 'completed' },
        { label: 'Access Revoked',      date: this.fmtDate(c.access_revoked_at),  done: !!c.access_revoked_at },
        { label: 'Last Working Day',    date: this.fmtDate(c.last_working_day),   done: c.last_working_day && new Date(c.last_working_day) < new Date() },
        { label: 'Completed',           date: this.fmtDate(c.completed_at),       done: !!c.completed_at },
      ]
    },
  },
  created() {
    this.loadCase()
    this.loadEmployees()
  },
  methods: {
    async loadCase() {
      this.loading = true
      try {
        const r = await axios.get(`exits/${this.$route.params.id}`)
        this.exitCase = r.data.data
        const toDateInput = v => v ? String(v).slice(0, 10) : ''
        this.editForm = {
          exit_date:          toDateInput(this.exitCase.exit_date),
          last_working_day:   toDateInput(this.exitCase.last_working_day),
          notice_period_days: this.exitCase.notice_period_days ?? '',
          reason:             this.exitCase.reason ?? '',
          confidential_notes: this.exitCase.confidential_notes ?? '',
        }
      } catch { this.toast.error('Failed to load exit case.') }
      finally { this.loading = false }
    },
    async loadEmployees() {
      try {
        const r = await axios.get('payroll/employees/active')
        this.employees = r.data.data ?? []
      } catch { /* non-fatal */ }
    },
    async switchTab(tab) {
      this.activeTab = tab
      if (tab === 'clearance' && !this.tasks.length) this.loadTasks()
      if (tab === 'kt' && !this.ktLogs.length) this.loadKt()
      if (tab === 'interview' && !this.interview.id) this.loadInterview()
      if (tab === 'fnf' && !this.fnfItems.length) this.loadFnf()
    },
    // ── Overview ─────────────────────────────────────────────────────────────
    async saveDetails() {
      this.savingDetails = true
      try {
        const r = await axios.put(`exits/${this.exitCase.id}`, this.editForm)
        this.exitCase = { ...this.exitCase, ...r.data.data }
        this.toast.success('Details updated.')
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to save.') }
      finally { this.savingDetails = false }
    },
    openCompleteModal() {
      this.deactivateOption = this.exitCase.last_working_day ? 'scheduled' : 'now'
      this.showCompleteModal = true
    },
    async doComplete() {
      this.completing = true
      try {
        const r = await axios.post(`exits/${this.exitCase.id}/transition`, {
          action:            'complete',
          deactivate_option: this.deactivateOption,
        })
        this.exitCase = { ...this.exitCase, ...r.data.data }
        this.showCompleteModal = false
        this.toast.success('Exit case completed.')
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to complete exit case.')
      } finally {
        this.completing = false
      }
    },
    async doTransition(action) {
      if (action === 'cancel' && !this.cancelReason.trim()) {
        this.toast.error('Please provide a cancellation reason.')
        return
      }
      try {
        const r = await axios.post(`exits/${this.exitCase.id}/transition`, { action, reason: this.cancelReason })
        this.exitCase = { ...this.exitCase, ...r.data.data }
        this.showCancelModal = false
        this.cancelReason = ''
        this.toast.success('Status updated.')
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to update status.') }
    },
    // ── Clearance ─────────────────────────────────────────────────────────────
    async loadTasks() {
      this.loadingTasks = true
      try {
        const r = await axios.get(`exits/${this.exitCase.id}/clearance`)
        this.tasks = r.data.data ?? []
      } catch { this.toast.error('Failed to load clearance tasks.') }
      finally { this.loadingTasks = false }
    },
    async addTask() {
      if (!this.newTask.department || !this.newTask.task_name) { this.toast.error('Department and task are required.'); return }
      this.savingTask = true
      try {
        const r = await axios.post(`exits/${this.exitCase.id}/clearance`, this.newTask)
        this.tasks.push(r.data.data)
        this.newTask = { department: '', task_name: '', assigned_to_employee_id: '' }
        this.showAddTask = false
        this.toast.success('Task added.')
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to add task.') }
      finally { this.savingTask = false }
    },
    async updateTask(task) {
      try {
        await axios.put(`exits/${this.exitCase.id}/clearance/${task.id}`, { status: task.status })
        this.toast.success('Task updated.')
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to update task.') }
    },
    // ── KT ───────────────────────────────────────────────────────────────────
    async loadKt() {
      this.loadingKt = true
      try {
        const r = await axios.get(`exits/${this.exitCase.id}/kt`)
        this.ktLogs = r.data.data ?? []
      } catch { this.toast.error('Failed to load KT logs.') }
      finally { this.loadingKt = false }
    },
    async addKt() {
      if (!this.newKt.to_employee_id || !this.newKt.task_description) { this.toast.error('Assign to and task are required.'); return }
      this.savingKt = true
      try {
        const r = await axios.post(`exits/${this.exitCase.id}/kt`, this.newKt)
        this.ktLogs.push(r.data.data)
        this.newKt = { to_employee_id: '', task_description: '', due_date: '' }
        this.showAddKt = false
        this.toast.success('KT task added.')
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to add KT task.') }
      finally { this.savingKt = false }
    },
    async updateKt(log) {
      try {
        await axios.put(`exits/${this.exitCase.id}/kt/${log.id}`, { status: log.status })
        this.toast.success('KT task updated.')
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to update KT task.') }
    },
    // ── Interview ─────────────────────────────────────────────────────────────
    async loadInterview() {
      this.loadingInterview = true
      try {
        const r = await axios.get(`exits/${this.exitCase.id}/interview`)
        this.interview = r.data.data ?? { status: 'scheduled', overall_rating: null, notes: '', scheduled_at: '', conducted_at: '' }
      } catch { this.toast.error('Failed to load interview.') }
      finally { this.loadingInterview = false }
    },
    async saveInterview() {
      this.savingInterview = true
      try {
        const r = await axios.put(`exits/${this.exitCase.id}/interview`, this.interview)
        this.interview = r.data.data
        this.toast.success('Interview saved.')
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to save.') }
      finally { this.savingInterview = false }
    },
    // ── FnF ──────────────────────────────────────────────────────────────────
    async loadFnf() {
      this.loadingFnf = true
      try {
        const r = await axios.get(`exits/${this.exitCase.id}/fnf`)
        this.fnfItems   = r.data.data?.items ?? []
        this.fnfSummary = { earnings: r.data.data?.earnings ?? 0, deductions: r.data.data?.deductions ?? 0, net: r.data.data?.net ?? 0 }
      } catch { this.toast.error('Failed to load F&F data.') }
      finally { this.loadingFnf = false }
    },
    async addFnf() {
      if (!this.newFnf.description || !this.newFnf.amount) { this.toast.error('Description and amount are required.'); return }
      this.savingFnf = true
      try {
        const r = await axios.post(`exits/${this.exitCase.id}/fnf`, this.newFnf)
        this.fnfItems.push(r.data.data)
        this.recalcSummary()
        this.newFnf = { description: '', type: 'earning', amount: '' }
        this.showAddFnf = false
        this.toast.success('Item added.')
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to add item.') }
      finally { this.savingFnf = false }
    },
    async deleteFnf(itemId) {
      try {
        await axios.delete(`exits/${this.exitCase.id}/fnf/${itemId}`)
        this.fnfItems = this.fnfItems.filter(i => i.id !== itemId)
        this.recalcSummary()
        this.toast.success('Item removed.')
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to delete.') }
    },
    async processFnf() {
      this.processingFnf = true
      try {
        await axios.post(`exits/${this.exitCase.id}/fnf/process`)
        this.toast.success('F&F processed.')
        await this.loadFnf()
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to process.') }
      finally { this.processingFnf = false }
    },
    recalcSummary() {
      const e = this.fnfItems.filter(i => i.type === 'earning').reduce((s, i) => s + +i.amount, 0)
      const d = this.fnfItems.filter(i => i.type === 'deduction').reduce((s, i) => s + +i.amount, 0)
      this.fnfSummary = { earnings: e, deductions: d, net: e - d }
    },
    // ── Helpers ───────────────────────────────────────────────────────────────
    fullName(emp) { return emp ? `${emp.first_name} ${emp.last_name}` : '—' },
    initials(emp) { return emp ? `${emp.first_name?.[0] ?? ''}${emp.last_name?.[0] ?? ''}`.toUpperCase() : '?' },
    fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—' },
    fmtAmt(v) { return Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
    typeLabel(t) {
      const m = { resignation:'Resignation', termination:'Termination', retirement:'Retirement', redundancy:'Redundancy', death:'Death' }
      return m[t] || t
    },
    statusLabel(s) {
      const m = { draft:'Draft', pending_approval:'Pending Approval', approved:'Approved', in_progress:'In Progress',
                  clearance:'Clearance', fnf_pending:'F&F Pending', completed:'Completed', cancelled:'Cancelled' }
      return m[s] || s
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:16px; }
.header-left { display:flex; align-items:center; gap:12px; }
.back-btn    { width:36px; height:36px; border-radius:8px; border:1px solid #e4eaf2; background:#fff; cursor:pointer; font-size:14px; color:#4a5e78; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.back-btn:hover { background:#f8fafc; }
.case-title  { display:flex; align-items:center; gap:8px; margin-bottom:3px; }
.case-no     { font-size:18px; font-weight:700; color:#1a2b45; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.header-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 16px; height:36px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#004AAD; font-size:13px; font-weight:600; padding:0 14px; height:36px; border-radius:8px; border:1.5px solid #004AAD; cursor:pointer; font-family:inherit; }
.btn-outline:hover { background:#f0f4ff; }
.btn-action { background:#004AAD; color:#fff; font-size:12px; font-weight:600; padding:0 14px; height:34px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-action:hover { background:#003b8e; }
.btn-success { background:#16a34a; color:#fff; font-size:12px; font-weight:600; padding:0 14px; height:34px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; display:inline-flex; align-items:center; gap:6px; }
.btn-success:disabled { opacity:.6; cursor:not-allowed; }
.btn-danger  { background:#ef4444; color:#fff; font-size:13px; font-weight:600; padding:0 16px; height:36px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-danger-outline { background:#fff; color:#ef4444; font-size:12px; font-weight:600; padding:0 14px; height:34px; border-radius:8px; border:1.5px solid #ef4444; cursor:pointer; font-family:inherit; }
.btn-danger-outline:hover { background:#fee2e2; }
.btn-del { background:none; border:none; color:#ef4444; cursor:pointer; font-size:14px; padding:2px 6px; }
.btn-sm-primary { background:#004AAD; color:#fff; font-size:12px; font-weight:600; padding:0 12px; height:32px; border-radius:7px; border:none; cursor:pointer; font-family:inherit; }

.emp-strip { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:16px 20px; margin-bottom:16px; display:flex; align-items:center; gap:16px; flex-wrap:wrap; }
.emp-avatar-lg { width:48px; height:48px; border-radius:50%; background:#004AAD; color:#fff; font-size:16px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.emp-info { flex:1; min-width:140px; }
.emp-name-lg { font-size:15px; font-weight:700; color:#1a2b45; }
.emp-meta    { font-size:12px; color:#7a90b0; }
.dot { margin:0 4px; }
.kpi-pills  { display:flex; gap:16px; flex-wrap:wrap; margin-left:auto; }
.kpi-pill   { text-align:center; }
.kpi-label  { font-size:10px; color:#7a90b0; text-transform:uppercase; letter-spacing:.04em; margin-bottom:3px; }
.kpi-value  { font-size:13px; font-weight:700; color:#1a2b45; }

.tabs-row { display:flex; gap:2px; border-bottom:2px solid #e4eaf2; margin-bottom:16px; overflow-x:auto; }
.tab-btn  { padding:10px 16px; font-size:13px; font-weight:600; color:#7a90b0; background:none; border:none; cursor:pointer; font-family:inherit; border-bottom:2px solid transparent; margin-bottom:-2px; white-space:nowrap; }
.tab-btn:hover { color:#004AAD; }
.tab-btn--active { color:#004AAD; border-bottom-color:#004AAD; }

.tab-body { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:20px; }
.tab-top-bar { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; }
.tab-title { font-size:14px; font-weight:700; color:#1a2b45; }

.detail-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:20px; }
@media(max-width:600px) { .detail-grid { grid-template-columns:1fr; } }
.detail-block { background:#f8fafc; border-radius:10px; padding:14px; }
.block-label  { font-size:11px; font-weight:700; color:#7a90b0; text-transform:uppercase; margin-bottom:5px; }
.block-val    { font-size:13px; color:#1a2b45; }

.edit-section { border-top:1px solid #f0f4f8; padding-top:20px; margin-top:16px; }
.section-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin-bottom:14px; }
.form-row  { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }
.form-input:focus { border-color:#004AAD; }
.form-input:disabled { background:#f8fafc; color:#aab8cc; }
.form-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
@media(max-width:540px) { .form-grid-2 { grid-template-columns:1fr; } }

.task-card  { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:12px 0; border-bottom:1px solid #f0f4f8; }
.task-card:last-of-type { border-bottom:none; }
.task-left  { display:flex; flex-direction:column; gap:3px; flex:1; }
.task-dept  { font-size:10px; font-weight:700; color:#7a90b0; text-transform:uppercase; }
.task-name  { font-size:13px; font-weight:600; color:#1a2b45; }
.task-assign { font-size:11px; color:#7a90b0; }
.task-right { display:flex; align-items:center; gap:8px; }
.task-status { font-size:10px; font-weight:700; padding:2px 8px; border-radius:6px; text-transform:uppercase; }
.task-st--pending   { background:#fef3c7; color:#92400e; }
.task-st--completed { background:#dcfce7; color:#166534; }
.task-st--waived    { background:#f1f5f9; color:#6b7280; }
.task-select { padding:5px 8px; border:1px solid #dde5f0; border-radius:6px; font-size:12px; font-family:inherit; outline:none; }
.clearance-summary { font-size:12px; color:#7a90b0; text-align:right; margin-top:12px; }
.cs-done { font-weight:700; color:#16a34a; }

.star-row { display:flex; gap:4px; }
.star-btn  { background:none; border:none; font-size:24px; color:#dde5f0; cursor:pointer; padding:0; transition:color .1s; }
.star-btn:hover,.star--on { color:#f59e0b; }

.fnf-table-wrap { overflow-x:auto; margin-bottom:16px; }
.fnf-table { width:100%; border-collapse:collapse; font-size:13px; }
.fnf-table th { background:#f8fafc; padding:8px 12px; text-align:left; font-size:11px; font-weight:700; color:#7a90b0; text-transform:uppercase; letter-spacing:.04em; border-bottom:1px solid #e4eaf2; }
.fnf-table td { padding:10px 12px; border-bottom:1px solid #f0f4f8; }
.empty-row  { text-align:center; color:#aab8cc; padding:24px; }
.fnf-type   { font-size:10px; font-weight:600; padding:2px 8px; border-radius:6px; text-transform:uppercase; }
.fnf--earning   { background:#dcfce7; color:#166534; }
.fnf--deduction { background:#fee2e2; color:#991b1b; }
.fnf-amt    { font-weight:700; }
.fnf-green  { color:#16a34a; }
.fnf-red    { color:#ef4444; }
.fnf-summary { border-top:2px solid #e4eaf2; padding-top:12px; }
.fnf-row    { display:flex; justify-content:space-between; font-size:13px; padding:4px 0; }
.fnf-net    { font-weight:700; font-size:15px; border-top:1px solid #e4eaf2; margin-top:6px; padding-top:8px; }

.timeline   { display:flex; flex-direction:column; gap:0; }
.tl-item    { display:flex; gap:16px; align-items:flex-start; padding-bottom:20px; position:relative; }
.tl-item::before { content:''; position:absolute; left:8px; top:20px; bottom:0; width:2px; background:#e4eaf2; }
.tl-item:last-child::before { display:none; }
.tl-dot     { width:18px; height:18px; border-radius:50%; background:#e4eaf2; border:2px solid #fff; box-shadow:0 0 0 2px #e4eaf2; flex-shrink:0; margin-top:2px; }
.tl-dot--done { background:#004AAD; box-shadow:0 0 0 2px #004AAD; }
.tl-content { flex:1; }
.tl-label   { font-size:13px; font-weight:600; color:#1a2b45; }
.tl-date    { font-size:11px; color:#7a90b0; }

.status-badge { font-size:11px; font-weight:600; padding:3px 9px; border-radius:6px; }
.status--draft           { background:#f1f5f9; color:#475569; }
.status--pending_approval { background:#fef3c7; color:#92400e; }
.status--approved        { background:#dcfce7; color:#166534; }
.status--in_progress     { background:#dbeafe; color:#1e40af; }
.status--clearance       { background:#fce7f3; color:#9d174d; }
.status--fnf_pending     { background:#fee2e2; color:#991b1b; }
.status--completed       { background:#dcfce7; color:#166534; }
.status--cancelled       { background:#f1f5f9; color:#6b7280; }

.type-badge { font-size:10px; font-weight:600; padding:3px 8px; border-radius:6px; }
.type--resignation  { background:#fef3c7; color:#92400e; }
.type--termination  { background:#fee2e2; color:#991b1b; }
.type--retirement   { background:#e0f2fe; color:#0369a1; }
.type--redundancy   { background:#f3e8ff; color:#6b21a8; }
.type--death        { background:#f1f5f9; color:#475569; }

.empty-state { text-align:center; padding:32px 0; color:#aab8cc; font-size:13px; }

.dlg-overlay { position:fixed; inset:0; background:rgba(0,0,0,.4); display:flex; align-items:center; justify-content:center; z-index:1050; padding:16px; }
.dlg-box { background:#fff; border-radius:14px; padding:24px; width:100%; max-width:440px; }
.radio-group { display:flex; flex-direction:column; gap:10px; }
.radio-option { display:flex; align-items:flex-start; gap:8px; font-size:13px; color:#1a2b45; cursor:pointer; }
.radio-option input[type="radio"] { margin-top:2px; flex-shrink:0; }
.radio-option--disabled { opacity:.45; cursor:not-allowed; }
.opt-date { font-weight:600; color:#004AAD; }
.dlg-title { font-size:16px; font-weight:700; color:#1a2b45; margin-bottom:16px; }
.dlg-actions { display:flex; gap:10px; justify-content:flex-end; margin-top:20px; }

.sk-page { }
.sk-header { height:80px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-radius:14px; margin-bottom:14px; }
.sk-body   { height:300px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-radius:14px; }
.sk-list   { display:flex; flex-direction:column; gap:10px; }
.sk-item   { height:52px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-radius:8px; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
</style>
